import { ActivityTrackerService } from './activity-tracker.service';
import { LocalStorageManagerService } from '@services/data-managers/local-storage-manager.service';
import { NotificationsService } from '@services/notifications';
import { UserManagerService } from './data-managers/user-manager.service';
import { AuthService } from './auth/auth.service';
import { ApiService } from './api.service';
import { MetaService } from './meta.service';
import {
	Injectable,
	inject,
	effect,
	OnDestroy,
	signal,
	WritableSignal,
	untracked,
} from '@angular/core';
import { Subject, Observable, Subscription, timer, of } from 'rxjs';
import { switchMap, catchError, filter } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ITicker } from '@models/binance/ticker-socket';
import { IUser } from '@models/cms/user';
import { NotificationList } from '@models/notification';
import { SignalResponse } from '@models/sentiment/signal';
import { AlertNotification } from '@models/alert';
import { InvestingAlertPayload } from '@models/investing';

// WebSocket message types for Rust API
interface WsClientMessage {
	type:
		| 'subscribe'
		| 'unsubscribe'
		| 'subscribenotifications'
		| 'unsubscribenotifications'
		| 'subscribesignals'
		| 'unsubscribesignals'
		| 'subscribealerts'
		| 'unsubscribealerts'
		| 'ping';
	/** ping: czy użytkownik jest aktywny (Cryptionis Token). */
	active?: boolean;
	symbols?: string[];
	interval?: number;
	category?: string;
	symbol?: string;
}

interface WsServerMessage {
	type:
		| 'subscribed'
		| 'unsubscribed'
		| 'notificationssubscribed'
		| 'notificationsunsubscribed'
		| 'notification'
		| 'signalssubscribed'
		| 'signalsunsubscribed'
		| 'signalupdate'
		| 'alertssubscribed'
		| 'alertsunsubscribed'
		| 'alertupdate'
		| 'investingalertssubscribed'
		| 'investingalertsunsubscribed'
		| 'investingalertupdate'
		| 'pong'
		| 'error';
	symbols?: string[];
	interval?: number;
	category?: string;
	message?: string;
	notification_type?: string;
	changes?: NotificationList['changes'];
	data?: Record<string, unknown>;
}

/** Response from GET /market/tickers/live */
interface LiveTickerResponse {
	symbol: string;
	price: string;
	bid_price: string;
	ask_price: string;
	open_price: string;
	high_price: string;
	low_price: string;
	volume: string;
	quote_volume: string;
	price_change: string;
	price_change_percent: string;
	trades_count: number;
}

@Injectable({
	providedIn: 'root',
})
export class SocketService implements OnDestroy {
	private readonly _us = inject(UserManagerService);
	private readonly _ss = inject(LocalStorageManagerService);
	private readonly _ns = inject(NotificationsService);
	private readonly _auth = inject(AuthService);
	private readonly _api = inject(ApiService);
	private readonly _meta = inject(MetaService);
	private readonly _activity = inject(ActivityTrackerService);

	binanceTickerList: Observable<ITicker[]>;

	private _binanceTickerSubject = new Subject<ITicker[]>();
	private _binanceWsSub: Subscription | null = null;

	user: IUser | null = null;

	/** Latest real-time volume notification received via WebSocket (P4) */
	latestVolumeNotification: WritableSignal<NotificationList | null> =
		signal<NotificationList | null>(null);

	/** Latest real-time signal received via WebSocket */
	latestSignal: WritableSignal<SignalResponse | null> = signal<SignalResponse | null>(null);

	/** Whether signals subscription is active */
	signalsSubscribed = false;

	/** Latest real-time alert notification received via WebSocket */
	latestAlert: WritableSignal<AlertNotification | null> = signal<AlertNotification | null>(null);

	/** Latest real-time investing alert received via WebSocket */
	latestInvestingAlert: WritableSignal<InvestingAlertPayload | null> =
		signal<InvestingAlertPayload | null>(null);

	/** Whether alerts subscription is active */
	alertsSubscribed = false;
	/** Someone in the app wants alerts — re-sent on every (re)connect. */
	private alertsWanted = false;

	private notificationsWs: WebSocket | null = null;
	private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
	/** Odstęp ponownego łączenia — rośnie po każdej porażce (5 s → 60 s), zeruje się po
	 *  udanym połączeniu. Stałe 5 s zasypywało konsolę błędami, gdy /ws był niedostępny. */
	private reconnectDelayMs = 5000;

	/** Czy kanał /ws jest połączony — bez niego nie liczą się minuty Cryptionis Token. */
	readonly connected = signal(false);
	private pingInterval: ReturnType<typeof setInterval> | null = null;

	constructor() {
		this.binanceTickerList = this._binanceTickerSubject.asObservable();
		this._connectBinanceWebSocket();

		// Połączenie /ws (powiadomienia, alerty, ping aktywności dla Cryptionis Token) zależy od
		// PRAWDZIWEJ sesji: zalogowany użytkownik + token dostępu. Wcześniej efekt patrzył na
		// atrapę `UserManagerService` (ustawianą raz, pierwsza wartość pomijana), więc
		// połączenie nie powstawało wcale — otwierała je dopiero zmiana interwału powiadomień.
		effect(() => {
			const authUser = this._auth.user();
			const token = this._auth.accessToken();

			untracked(() => {
				if (!authUser) {
					this.user = null;
					this.closeNotificationsConnection();
					return;
				}

				this.user = this._us.user();
				const open =
					this.notificationsWs &&
					(this.notificationsWs.readyState === WebSocket.OPEN ||
						this.notificationsWs.readyState === WebSocket.CONNECTING);

				if (token && !open) {
					this.createNotificationsConnection();
				}
			});
		});
	}

	private _connectBinanceWebSocket(): void {
		this._binanceWsSub?.unsubscribe();

		// Poll backend /market/tickers/live. Interval is adaptive:
		//  - 10s when /api/meta says read_only_market_data (dev pulling prod
		//    data through SSH tunnel — every miss costs ~3s + 900KB),
		//  - 4s in normal mode. Was 2s (30 req/min), but this is the app's dominant
		//    continuous poller and the upstream Cloudflare rate-limit tripped on the
		//    dashboard's aggregate request rate → 429 on this endpoint. 4s (15 req/min)
		//    halves it and stays well under the limit; 4s price updates are imperceptible
		//    for the dashboard (the collector still writes CH every ~1s, so no data lag).
		// 6 s (10 req/min) — a further ~33% cut vs 4 s; still imperceptible for the dashboard
		// (the collector writes CH ~every 1 s) and now backed by a 60 s server cache, so most
		// polls are cache hits anyway.
		const pollMs = this._meta.readOnlyMarketData() ? 10_000 : 6_000;
		let backoffMs = 0;
		this._binanceWsSub = timer(0, pollMs)
			.pipe(
				filter(() => {
					if (backoffMs > 0) {
						backoffMs = Math.max(0, backoffMs - pollMs);
						return false;
					}

					return true;
				}),
				switchMap(() =>
					this._api.sendGET<LiveTickerResponse[]>('market/tickers/live').pipe(
						catchError((err): Observable<LiveTickerResponse[]> => {
							if (err?.status === 429 || err?.status === 401) {
								backoffMs = Math.min(backoffMs + 10000, 60000);
							}

							return of([]);
						}),
					),
				),
			)
			.subscribe((tickers: LiveTickerResponse[]) => {
				if (tickers && tickers.length > 0) {
					const mapped: ITicker[] = tickers.map((t) => ({
						e: '24hrTicker',
						E: Date.now(),
						s: t.symbol,
						p: parseFloat(t.price_change) || 0,
						P: parseFloat(t.price_change_percent) || 0,
						w: 0,
						x: 0,
						c: parseFloat(t.price) || 0,
						Q: 0,
						b: parseFloat(t.bid_price) || 0,
						B: 0,
						a: parseFloat(t.ask_price) || 0,
						A: 0,
						o: parseFloat(t.open_price) || 0,
						h: parseFloat(t.high_price) || 0,
						l: parseFloat(t.low_price) || 0,
						v: parseFloat(t.volume) || 0,
						q: parseFloat(t.quote_volume) || 0,
						O: 0,
						C: Date.now(),
						F: 0,
						L: 0,
						n: t.trades_count || 0,
					}));
					this._binanceTickerSubject.next(mapped);
				}
			});
	}

	ngOnDestroy(): void {
		this.closeNotificationsConnection();
		this._binanceWsSub?.unsubscribe();
		this._binanceTickerSubject.complete();
	}

	createNotificationsConnection(): void {
		if (this.user == null) {
			return;
		}

		const token = this._auth.getAccessToken();

		if (!token) {
			return;
		}

		this.closeNotificationsConnection();

		const interval = this._ss.intervalNotifications ?? 300;

		const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
		const wsHost = (environment as Record<string, unknown>).SOCKET_API_HOST
			? String((environment as Record<string, unknown>).SOCKET_API_HOST)
			: environment.production
			? window.location.host
			: 'localhost:8081';
		// Token is NOT in the URL — sent as the first WebSocket message to avoid log exposure
		const wsUrl = `${wsProtocol}//${wsHost}/ws`;

		try {
			this.notificationsWs = new WebSocket(wsUrl);

			this.notificationsWs.onopen = () => {
				this.connected.set(true);
				this.reconnectDelayMs = 5000;
				// Authenticate first before any subscriptions
				this.notificationsWs!.send(JSON.stringify({ type: 'auth', token }));
				this.subscribeToNotifications(interval);
				// Alerts are wanted app-wide once anyone asked — (re)subscribe on every open,
				// including reconnects (a subscription sent before OPEN used to be dropped).
				if (this.alertsWanted) {
					this.notificationsWs!.send(JSON.stringify({ type: 'subscribealerts' }));
				}
				this.startPingInterval();
			};

			this.notificationsWs.onmessage = (event) => {
				try {
					const msg = JSON.parse(event.data) as WsServerMessage;

					if (
						msg.type === 'notification' &&
						msg.notification_type === 'volume' &&
						msg.changes
					) {
						const notificationList: NotificationList = {
							type: 'volume',
							changes: msg.changes,
						};
						this._ns.updateVolumeNotifications(notificationList.changes);
						this.latestVolumeNotification.set(notificationList);
					} else if (msg.type === 'signalupdate' && msg.data) {
						// Real-time signal update from backend signal engine
						this.latestSignal.set(msg.data as unknown as SignalResponse);
					} else if (msg.type === 'signalssubscribed') {
						this.signalsSubscribed = true;
					} else if (msg.type === 'signalsunsubscribed') {
						this.signalsSubscribed = false;
					} else if (msg.type === 'alertupdate' && msg.data) {
						this.latestAlert.set(msg.data as unknown as AlertNotification);
					} else if (msg.type === 'alertssubscribed') {
						this.alertsSubscribed = true;
					} else if (msg.type === 'alertsunsubscribed') {
						this.alertsSubscribed = false;
					} else if (msg.type === 'investingalertupdate' && msg.data) {
						this.latestInvestingAlert.set(msg.data as unknown as InvestingAlertPayload);
					} else if (msg.type === 'error') {
						// WebSocket server reported an error
					}
				} catch {
					// Failed to parse WebSocket message
				}
			};

			this.notificationsWs.onclose = () => {
				this.connected.set(false);
				this.stopPingInterval();

				if (this.user != null) {
					const delay = this.reconnectDelayMs;
					this.reconnectDelayMs = Math.min(delay * 2, 60_000);
					this.reconnectTimeout = setTimeout(() => {
						this.createNotificationsConnection();
					}, delay);
				}
			};

			this.notificationsWs.onerror = () => {
				// WebSocket connection error - will trigger onclose for reconnect
			};
		} catch {
			// Failed to create WebSocket connection
		}
	}

	private subscribeToNotifications(intervalSeconds: number): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = {
				type: 'subscribenotifications',
				interval: intervalSeconds,
			};
			this.notificationsWs.send(JSON.stringify(msg));
		}
	}

	private startPingInterval(): void {
		this.stopPingInterval();
		this._activity.start();
		this.pingInterval = setInterval(() => {
			if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
				// `active` zalicza minutę do Cryptionis Token (serwer: najwyżej 1 min/min).
				const msg: WsClientMessage = { type: 'ping', active: this._activity.isActive() };
				this.notificationsWs.send(JSON.stringify(msg));
			}
		}, 30000); // Ping every 30 seconds
	}

	private stopPingInterval(): void {
		if (this.pingInterval) {
			clearInterval(this.pingInterval);
			this.pingInterval = null;
		}
	}

	private closeNotificationsConnection(): void {
		this.stopPingInterval();

		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		if (this.notificationsWs) {
			this.notificationsWs.close();
			this.notificationsWs = null;
		}
	}

	reconnectWithInterval(intervalSeconds: number): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			this.subscribeToNotifications(intervalSeconds);
		} else {
			this.createNotificationsConnection();
		}
	}

	/**
	 * Subscribe to real-time signal updates via WebSocket.
	 * Optionally filter by category (zone, mtf_cascade, divergence, anomaly, momentum).
	 */
	subscribeToSignals(category?: string): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = {
				type: 'subscribesignals',
				...(category ? { category } : {}),
			};
			this.notificationsWs.send(JSON.stringify(msg));
		}
	}

	/**
	 * Unsubscribe from real-time signal updates.
	 */
	unsubscribeFromSignals(): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = { type: 'unsubscribesignals' };
			this.notificationsWs.send(JSON.stringify(msg));
		}

		this.signalsSubscribed = false;
	}

	/**
	 * Subscribe to real-time alert notifications via WebSocket.
	 * Alerts are user-specific (filtered by backend via user_id from JWT).
	 */
	subscribeToAlerts(): void {
		this.alertsWanted = true;
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = { type: 'subscribealerts' };
			this.notificationsWs.send(JSON.stringify(msg));
		}
	}

	/**
	 * Unsubscribe from real-time alert notifications.
	 */
	unsubscribeFromAlerts(): void {
		this.alertsWanted = false;
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = { type: 'unsubscribealerts' };
			this.notificationsWs.send(JSON.stringify(msg));
		}

		this.alertsSubscribed = false;
	}

	/**
	 * Subscribe to real-time investing alerts via WebSocket.
	 * Investing alerts are score-threshold triggered setup notifications.
	 */
	subscribeToInvestingAlerts(): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = { type: 'subscribealerts' };
			this.notificationsWs.send(JSON.stringify(msg));
		}
	}

	/**
	 * Unsubscribe from real-time investing alerts.
	 */
	unsubscribeFromInvestingAlerts(): void {
		if (this.notificationsWs && this.notificationsWs.readyState === WebSocket.OPEN) {
			const msg: WsClientMessage = { type: 'unsubscribealerts' };
			this.notificationsWs.send(JSON.stringify(msg));
		}
	}
}
